/* `/providers` — the workspace's provider connections, the models they can run,
   and the default for new agents. C2 of `tasks/byok-providers/00-PLAN.md`; the
   page is backend/modules/providers/README.md.

   In `shared/api/` because settings, agents, chat, home and teams all read it.
   `toModelsWorkspace` is the one reader of the wire.

   THE KEY. A pasted provider key is form input for the length of one request. It
   rides in that request's body and nowhere else: never mutation variables (the
   cache would keep them), never the query cache, never React state. It waits in
   a ref for its request and is blanked in `finally`; responses carry only its
   last four characters. */
import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { useSettings } from "./settings";
import { selectionProblem, toModelChoice, toSelection } from "@/shared/lib/models";

import type {
  ApiConnection, ApiModelChoice, ApiProviderModel, ApiProviders, ConnectionDraft,
  ModelOption, ModelSelection, ModelsWorkspace, ProviderConnection,
} from "@/shared/types/models";

export const modelKeys = { all: ["models"] as const, workspace: (orgId: string) => ["models", orgId] as const };

/* ---------------------------- the wire → the app ---------------------------- */

const CAPABILITIES = [["tools", "Tools"], ["image", "Vision"], ["document", "PDF"], ["thinking", "Reasoning"], ["image_generation", "Makes images"], ["decision", "Decisions only"]] as const;

const toModelOption = (m: ApiProviderModel): ModelOption => ({
  id: m.id,
  name: m.name,
  family: m.family,
  provider: m.provider,
  capabilities: CAPABILITIES.filter(([feature]) => m.features.includes(feature)).map(([, label]) => label),
  maxOutputTokens: m.max_output,
  contextWindow: m.context,
  temperature: m.temperature ? { min: 0, max: 1 } : null,
  price: { input: m.price.input, output: m.price.output, cacheRead: m.price.cache_read, cacheWrite: m.price.cache_write },
  deprecated: m.deprecated,
  decisionsOnly: m.features.includes("decision"),
  tier: m.tier ?? null,
  takesEffort: m.features.includes("effort"),
});

export const toConnection = (c: ApiConnection): ProviderConnection => ({
  id: c.id,
  provider: c.provider,
  name: c.name,
  suffix: c.key_suffix,
  status: c.status,
  checkedAt: c.checked_at,
  modelIds: c.model_ids,
  hiddenCount: c.hidden_count,
});

/** `GET /providers` as the app reads it. */
export const toModelsWorkspace = (body: ApiProviders): ModelsWorkspace => ({
  models: body.models.map(toModelOption),
  // an API from before Definable's models has none
  definable: (body.definable ?? []).map(toModelOption),
  connections: body.connections.map(toConnection),
  defaultSelection: body.default && toSelection(body.default),
});

/* -------------------------------- endpoints --------------------------------- */

export async function getModels(): Promise<ModelsWorkspace> {
  return toModelsWorkspace(unwrap(await client.get<Envelope<ApiProviders>>("/providers")));
}

/** Adds a connection, or with `connectionId` replaces its key; name and provider
    stay. The provider checks the key first, which can take ~10 s. The draft's
    key is blanked whatever the answer. */
export async function saveConnection(draft: ConnectionDraft, connectionId?: string): Promise<ProviderConnection> {
  try {
    const res = connectionId
      ? await client.put<Envelope<ApiConnection>>(`/providers/connections/${connectionId}`, { key: draft.key })
      : await client.post<Envelope<ApiConnection>>("/providers/connections", draft);
    return toConnection(unwrap(res));
  } finally {
    draft.key = "";
  }
}

export type ModelAction =
  | { type: "default"; selection: ModelSelection }
  | { type: "clear-default" }
  | { type: "remove" | "verify"; id: string }
  | { type: "rename"; id: string; name: string };

/** Every write but a key's. A refusal is thrown by the client as an ApiError. */
export async function changeModels(action: ModelAction): Promise<void> {
  if (action.type === "default") {
    unwrap(await client.put<Envelope<ApiModelChoice>>("/providers/default", toModelChoice(action.selection)));
    return;
  }
  if (action.type === "clear-default") {
    await client.delete("/providers/default"); // 204 and no body
    return;
  }
  const path = `/providers/connections/${action.id}`;
  if (action.type === "rename") unwrap(await client.patch<Envelope<ApiConnection>>(path, { name: action.name }));
  else if (action.type === "verify") unwrap(await client.post<Envelope<ApiConnection>>(`${path}/verify`));
  else await client.delete(path); // 204 and no body, so there is no envelope to read
}

/* ---------------------------------- hooks ----------------------------------- */

/** Keyed by org, so a workspace switch never shows the last one's connections.
    Silent: the picker and the settings pane draw their own failure. */
export function useModels() {
  const settings = useSettings();
  const org = settings.data?.org;
  const query = useQuery({
    queryKey: modelKeys.workspace(org?.id ?? ""),
    queryFn: getModels,
    enabled: !!org,
    meta: { silent: true },
  });
  return { ...query, orgId: org?.id, canManage: org?.role === "owner" || org?.role === "admin" };
}

/** THE model check — can this selection run? — for chat, home, new agent and
    teams alike: the backend's rule over the workspace's real connections, plus
    the two answers the data can't give while it isn't there. */
export function useModelCheck(): (selection: ModelSelection | null) => string | null {
  const { data, error } = useModels();
  return useCallback(
    (selection: ModelSelection | null) =>
      data ? selectionProblem(selection, data)
      : error ? "Models could not be loaded. Open the model picker and retry."
      : "Checking model access…",
    [data, error],
  );
}

/** Silent: the settings pane shows each refusal beside what was refused. */
export function useModelActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: changeModels,
    onSettled: () => qc.invalidateQueries({ queryKey: modelKeys.all }),
    meta: { silent: true },
  });
}

export function useSaveConnection() {
  const qc = useQueryClient();
  const draftRef = useRef<{ draft: ConnectionDraft; id?: string } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      const entry = draftRef.current;
      if (!entry) throw new Error("Nothing to save.");
      try {
        return await saveConnection(entry.draft, entry.id);
      } finally {
        entry.draft.key = "";
        draftRef.current = null;
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: modelKeys.all }),
    meta: { silent: true },
    gcTime: 0,
  });
  useEffect(() => () => {
    if (draftRef.current) draftRef.current.draft.key = "";
    draftRef.current = null;
  }, []);
  return {
    ...mutation,
    save: (draft: ConnectionDraft, id?: string) => {
      draftRef.current = { draft, id };
      return mutation.mutateAsync();
    },
  };
}
